import * as React from "react";
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Divider,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import JourneyCouponDrawer from "./JourneyCouponDrawer";

const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    // Example: "Apr 4, 2026"
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const buildDetails = (data) => {
    const coupon = data?.coupon || {};
    const auto = data?.autoCouponAllocation || {};

    const start = coupon?.validityStartDate;
    const end = coupon?.validityEndDate;

    const details = [];

    if (end) details.push(`Expires on ${formatDate(end)}`);

    // Coupon type line
    if (coupon?.isBogoCoupon || auto?.couponType === "BOGO") {
        details.push("BOGO offer: buy one and get one free (as per applicable items).");
    }

    // Order type
    if (coupon?.orderType) {
        details.push(`Applicable for order type: ${coupon.orderType}`);
    }

    // Usage type
    if (coupon?.usageType) {
        details.push(`Offer is applicable ${coupon.usageType === "ONE-TIME" ? "once per user" : "multiple times"}.`);
    }

    // Nth order info
    if (auto?.nthOrderNumber) {
        details.push(`Applicable on your ${auto.nthOrderNumber}${getOrdinal(auto.nthOrderNumber)} order.`);
    }

    // Min cart / order values if present
    if (auto?.minCartValue) details.push(`Minimum cart value: ₹${auto.minCartValue}`);
    if (auto?.minOrderAmount) details.push(`Minimum order amount: ₹${auto.minOrderAmount}`);
    if (auto?.maxOrderAmount) details.push(`Maximum order amount: ₹${auto.maxOrderAmount}`);

    // If your API provides T&C array, show them
    const tc =
        coupon?.termsAndConditions?.length
            ? coupon.termsAndConditions
            : data?.termsAndConditions?.length
                ? data.termsAndConditions
                : auto?.termsAndConditions?.length
                    ? auto.termsAndConditions
                    : [];

    // Add T&C if available
    tc.forEach((t) => {
        if (typeof t === "string" && t.trim()) details.push(t.trim());
    });

    // Safe fallbacks (so modal never looks empty)
    if (!details.length && (start || end)) {
        details.push(`Valid ${start ? `from ${formatDate(start)}` : ""}${start && end ? " to " : ""}${end ? formatDate(end) : ""}`.trim());
    }
    if (!details.length) details.push("Offer details will be shown here.");

    return details;
};

const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
};

export default function JourneyCouponModal({ open, onClose, data }) {
    const coupon = data?.coupon || {};
    const auto = data?.autoCouponAllocation || {};
    
    const title = coupon?.title;
    const subtitle = coupon?.description || auto?.description;

    const details = React.useMemo(() => buildDetails(data), [data]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.20)",
                },
            }}
        >
            <DialogContent
                sx={{
                    p: 0,
                    background: "#fff",
                }}
            >
                <Box sx={{ p: 3, position: "relative" }}>
                    {/* Close */}
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            color: "rgba(0,0,0,0.55)",
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {/* Header */}
                    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: 26,
                                    fontWeight: 800,
                                    lineHeight: 1.1,
                                    color: "#0b3b4b",
                                }}
                            >
                                {title}
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: 18,
                                    fontWeight: 500,
                                    color: "#156b7f",
                                }}
                            >
                                {subtitle}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Divider like the design */}
                    <Divider
                        sx={{
                            mt: 2,
                            borderColor: "rgba(11,59,75,0.65)",
                            borderBottomWidth: "2px",
                        }}
                    />

                    {/* Details */}
                    <Typography
                        sx={{
                            mt: 2,
                            fontSize: 18,
                            fontWeight: 700,
                            color: "rgba(0,0,0,0.55)",
                        }}
                    >
                        Details
                    </Typography>

                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.3 }}>
                        {details.map((line, idx) => (
                            <Box
                                component="li"
                                key={`${idx}-${line}`}
                                sx={{
                                    fontSize: 16,
                                    color: "rgba(0,0,0,0.55)",
                                    lineHeight: 1.35,
                                    mb: 0.6,
                                }}
                            >
                                {line}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}